import { useQuery } from '@tanstack/react-query';
import { pickableLocations } from '@/lib/hooks';
import { SCOPE_LABELS, type ScopeType } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { conversationName, useConversations, useLocations, useProjects, useTeams, useWorkspaceId } from '@/lib/hooks';
import { Select } from '@/components/ui';

export const roleKeys = { roles: (ws: string) => ['roles', ws] as const, grants: (ws: string, f: object) => ['role-grants', ws, f] as const, catalogue: (ws: string) => ['perm-catalogue', ws] as const };

export function useRoles() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: roleKeys.roles(ws ?? ''), queryFn: () => api.roles(ws!), enabled: Boolean(ws), staleTime: 30_000 });
}
export function useCatalogue() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: roleKeys.catalogue(ws ?? ''), queryFn: () => api.permissionCatalogue(ws!), enabled: Boolean(ws), staleTime: 10 * 60_000 });
}

/** Options for each scope type, for pickers. */
export function useScopeOptions() {
  const locations = useLocations().data?.locations ?? [];
  const { teams } = useTeams();
  const projects = useProjects().data?.projects ?? [];
  const conversations = useConversations().data?.conversations ?? [];
  const me = useAuth().userId;
  return {
    workspace: [{ id: '', name: 'Whole workspace' }],
    location: pickableLocations(locations).map(l => ({ id: l.id, name: l.name })),
    team: teams.map(t => ({ id: t.id, name: `${t.name}${t.kind === 'group' ? ' (group)' : ''}` })),
    project: projects.filter(p => !p.archived_at).map(p => ({ id: p.id, name: `${p.key} · ${p.name}` })),
    conversation: conversations.filter(c => c.kind === 'channel').map(c => ({ id: c.id, name: conversationName(c, me) })),
  } as Record<ScopeType, { id: string; name: string }[]>;
}

export const SCOPE_ORDER: ScopeType[] = ['workspace', 'location', 'team', 'project', 'conversation'];

/** Scope picker: type + resource. Value is a scope ref ("workspace" or "location:<id>"). */
export function ScopePicker({ value, onChange, allowed = SCOPE_ORDER, className }: { value: string; onChange: (v: string) => void; allowed?: ScopeType[]; className?: string }) {
  const opts = useScopeOptions();
  const [type, id] = value === 'workspace' ? ['workspace', ''] : value.split(':') as [ScopeType, string];
  return (
    <div className={`flex gap-2 ${className ?? ''}`}>
      <Select aria-label="Scope type" className="h-8 w-40 py-1" value={type} onChange={e => {
        const t = e.target.value as ScopeType;
        onChange(t === 'workspace' ? 'workspace' : `${t}:${opts[t][0]?.id ?? ''}`);
      }}>
        {allowed.map(s => <option key={s} value={s}>{s === 'workspace' ? 'Whole workspace' : SCOPE_LABELS[s]}</option>)}
      </Select>
      {type !== 'workspace' && (
        <Select aria-label="Scope" className="h-8 min-w-48 flex-1 py-1" value={id} onChange={e => onChange(`${type}:${e.target.value}`)}>
          {!opts[type as ScopeType].length && <option value="">None available</option>}
          {opts[type as ScopeType].map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select>
      )}
    </div>
  );
}

export function scopeLabel(scope: string, name?: string | null) {
  if (scope === 'workspace') return 'Whole workspace';
  const type = scope.split(':')[0] as ScopeType;
  return `${SCOPE_LABELS[type] ?? type}: ${name ?? 'unknown'}`;
}
